// src/components/dashboard/BankAccountsCard.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { showToast } from '../../utils/helpers';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';
import ConnectBankModal from '../modals/ConnectBankModal';

const BankAccountsCard = ({ onAccountConnected }) => {
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showConnectModal, setShowConnectModal] = useState(false);

  // Keep the latest callback without re-triggering fetchAccounts (the parent
  // may pass an inline function, which would change identity every render).
  const onAccountConnectedRef = useRef(onAccountConnected);
  useEffect(() => {
    onAccountConnectedRef.current = onAccountConnected;
  }, [onAccountConnected]);

  const fetchAccounts = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get(API_CONFIG.USER.EXISTING_ACCOUNTS);
      if (response.data.success) {
        const list = response.data.data || [];
        setAccounts(list);
        // Tell the parent when a bank account is already present (first
        // render included) so gated actions like a Continue button light up.
        if (list.length > 0 && onAccountConnectedRef.current) {
          onAccountConnectedRef.current();
        }
      } else {
        throw new Error(response.data.error || 'Failed to load accounts');
      }
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
      showToast('Failed to load bank accounts', 'error');
      setAccounts([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleAccountConnected = () => {
    fetchAccounts();
    if (onAccountConnectedRef.current) {
      onAccountConnectedRef.current();
    }
  };

  const handleDeleteAccount = async (account) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete ${account.bank_name} (${account.account_number})?`
    );
    if (!confirmed) return;

    try {
      const response = await api.delete(API_CONFIG.PAYMENTS.DELETE_ACCOUNT, {
        params: { account_id: account.id }
      });
      if (response.data.success) {
        showToast('Bank account deleted successfully', 'success');
        fetchAccounts();
      } else {
        throw new Error(response.data.error || 'Failed to delete bank account');
      }
    } catch (error) {
      console.error('Error deleting bank account:', error);
      showToast(error.response?.data?.error || 'Failed to delete bank account', 'error');
    }
  };

  const renderIcon = () => (
    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 6l9-4 9 4m-9 4v10m0 0a2 2 0 100-4m0 4a2 2 0 110-4m-6 0a2 2 0 100 4m0-4a2 2 0 110 4" />
    </svg>
  );

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              {renderIcon()}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Bank Accounts</h3>
              <p className="text-xs text-gray-500">Your connected bank accounts</p>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2].map((n) => (
              <div key={n} className="animate-pulse h-16 bg-gray-100 rounded-lg"></div>
            ))}
          </div>
        ) : accounts.length > 0 ? (
          <div className="space-y-3">
            {accounts.map((account) => (
              <div
                key={account.id}
                className="flex items-center justify-between gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-9 h-9 bg-white border border-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                    {renderIcon()}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 text-sm truncate">{account.bank_name}</span>
                      {account.is_default === 1 && (
                        <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-full">
                          Default
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {account.account_number} • {account.account_name}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteAccount(account)}
                  className="flex-shrink-0 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete bank account"
                  aria-label={`Delete ${account.bank_name} account`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              {renderIcon()}
            </div>
            <p className="text-gray-600 font-medium mb-1">No bank account connected</p>
            <p className="text-gray-400 text-sm mb-4">Connect a bank account to receive funds.</p>
            <button
              onClick={() => setShowConnectModal(true)}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-5 py-2.5 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Connect bank account
            </button>
          </div>
        )}
      </div>

      <ConnectBankModal
        show={showConnectModal}
        onHide={() => setShowConnectModal(false)}
        onAccountConnected={handleAccountConnected}
      />
    </>
  );
};

export default BankAccountsCard;
